import { confirmAsync } from '@/shared/dialogs'
import { useAppWorkspaceStore } from './appWorkspaceStore'
import { closeWorkspaceTab, resolveCloseableTabPath } from './closeWorkspaceTab'
import { disposePrintSessionByPath } from '@/runtime/print/printSessions'

/**
 * Close a workspace tab after an optional dirty confirm.
 * Returns the next navigate path, or null if the user cancelled.
 *
 * Resolves renames / stale closures first: "Save and close" on a NEW record
 * renames the tab in `onSaved` and then closes with the path captured before
 * that rename; hierarchy Discard OK must never silent-no-op.
 */
export async function confirmCloseWorkspaceTab(
  path: string,
  ask: (text: string) => Promise<boolean> = (t) => confirmAsync(t),
): Promise<string | null> {
  const target = resolveCloseableTabPath(path)
  if (target == null) {
    return closeWorkspaceTab(path)
  }
  const tab = useAppWorkspaceStore.getState().tabs.find((t) => t.path === target)
  if (tab?.isDirty) {
    const ok = await ask('Discard unsaved changes?')
    if (!ok) return null
  }
  // A print tab's model lives only in its session — closing the tab drops it.
  disposePrintSessionByPath(target)
  return closeWorkspaceTab(path)
}

/** True when any open workspace tab has unsaved edits. */
export function workspaceHasDirtyTabs(): boolean {
  return useAppWorkspaceStore.getState().tabs.some((t) => t.isDirty)
}
